import { useMemo, useState } from 'react'
import { ValueReferances } from '../utils'
import { isValidDate } from '@/utils/dateUtils'
import { errorToast } from '@/utils/toasters'
import { scrollToFirstError } from '@/utils/scrollToError'
import { Validations } from '@/utils/validations'
import { useTranslation } from 'react-i18next'

const getRealValue = (val) => val?.value ?? val ?? val

const useForm = () => {
  const { t } = useTranslation()
  const [formValues, setFormValue] = useState({})
  const [touchedFields, setTouchedFields] = useState({})
  const [isSubmitted, setIsSubmitted] = useState(false)

  const allErrors = useMemo(() => {
    const result = {}

    Object.entries(formValues).forEach(([key, item]) => {
      const value = getRealValue(item?.value)
      let errorMessage = null

      if (item?.isRequired && (Validations.isNull(value) || Validations.isUndefined(value) || (Validations.isString(value) && !value.trim()))) {
        errorMessage = t('required-field')
      }
    //   dealing with string
      else if (Validations.isString(value)) {
        if (item?.min && value.length < item.min)
          errorMessage = `${t('min-length')} ${item.min} ${t('characters')}`
        if (item?.max && value.length > item.max)
          errorMessage = `${t('max-length')} ${item.max} ${t('characters')}`
        if (item?.type === 'email' && value && !Validations.isMail(value))
          errorMessage = t('email-not-valid')
        if (item?.type === 'tel' && value && (!Validations.isTel(value) || value.length < 10))
          errorMessage = t('invalid-phone-number')
      } else if (item?.lang) {
        if (item.lang.toLowerCase() === 'ar' && !Validations.isAr(value))
          errorMessage = t('invalid-ar')
        if (item.lang.toLowerCase() === 'en' && !Validations.isEn(value))
          errorMessage = t('invalid-en')
      } else if (item?.isRequired && Validations.isArray(value) && !value?.length) {
        errorMessage = t('required-field')
      } else if (item?.isRequired && Validations.isObject(value) && !Validations.isArray(value) && !isValidDate(value) && !value?.value) {
        errorMessage = t('required-field')
      }

      if (errorMessage) result[key] = { isError: true, errorMessage }
    })

    return result
  }, [formValues, t])

  const errors = useMemo(() => {
    if (isSubmitted) return allErrors
    return Object.fromEntries(
      Object.entries(allErrors).filter(([key]) => touchedFields?.[key])
    )
  }, [allErrors, touchedFields, isSubmitted])

  const isValid = useMemo(() => Object.keys(allErrors).length === 0, [allErrors])

  const GetValueFromLookup = (dataObject = {}) => {
    const isLookup =
      Object.keys(dataObject).includes('value') || Object.keys(dataObject).includes('label')
    return typeof dataObject === 'object' && isLookup ? dataObject?.value : dataObject
  }

  const GetValueFromField = (value) => {
    if (Array.isArray(value)) return value.map(GetValueFromLookup)
    if (typeof value === 'object' && !isValidDate(value)) return GetValueFromLookup(value || {})
    return value
  }

  const formDataResult = useMemo(() => {
    const finalFormValue = {}
    Object.entries(formValues).forEach(([key, val]) => {
      finalFormValue[key] = GetValueFromField(val?.value)
    })
    return finalFormValue
  }, [formValues])

  const onSubmit = (formAction) => {
    setIsSubmitted(true)
    if (!isValid) {
      errorToast('error-validation')
      scrollToFirstError()
      return
    }
    formAction(formDataResult)
  }

  const onReset = () => {
    setFormValue(ValueReferances(formValues)?.cleared)
    setTouchedFields({})
    setIsSubmitted(false)
  }

  const handleFormValues = (key, val, options = {}) => {
    const { markAsTouched = true } = options
    setFormValue(prev => ({ ...prev, [key]: val }))
    if (markAsTouched) setTouchedFields(prev => ({ ...prev, [key]: true }))
  }

  return {
    onSubmit,
    onReset,
    isSubmitted,
    isValid,
    formHandler: { formValues, handleFormValues, touchedFields, errors },
    formDataResult,
  }
}

export default useForm